import { Link } from "react-router-dom";
import { LOGO_URL } from "@/data/news";
import { Facebook, Youtube, Instagram, MessageCircle } from "lucide-react";
import { XIcon } from "@/components/site/Share";
import { POLICY_ORDER, POLICIES } from "@/data/policies";
import GoogleFollow from "@/components/site/GoogleFollow";

// Anchors on the homepage.
const SITE_LINKS = [
  { label: "About Us", href: "/#about", testKey: "about-us" },
  { label: "Leadership", href: "/#leadership", testKey: "leadership" },
  { label: "Join Us", href: "/#join", testKey: "join-us" },
  { label: "Contact", href: "/#contact", testKey: "contact" },
];

// Policies (dedicated pages).
const POLICY_LINKS = POLICY_ORDER.map((k) => ({
  label: POLICIES[k].footerLabel,
  href: POLICIES[k].href,
  testKey: k,
}));

const SOCIALS = [
  { label: "WhatsApp Channel", href: "https://whatsapp.com/channel/0029VaIKGvg11ulPVUkjh223", Icon: MessageCircle },
  { label: "Facebook", href: "https://www.facebook.com/denvapost", Icon: Facebook },
  { label: "X (Twitter)", href: "https://x.com/denvapostnews", Icon: XIcon },
  { label: "YouTube", href: "https://www.youtube.com/@denvapost", Icon: Youtube },
  { label: "Instagram", href: "https://www.instagram.com/denvapost?utm_source=qr&igsh=a3cwMjNrbDZjOWll", Icon: Instagram },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A0A0A] text-[#F4F4F0] relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-16 md:pt-28 pb-10">
        {/* Giant wordmark */}
        <div className="mb-12 md:mb-20">
          <h3 className="font-serif-dev font-black leading-[0.9] text-[36px] xs:text-[48px] sm:text-[92px] md:text-[150px] lg:text-[200px] tracking-tight break-words">
            देनवा <span className="italic text-[#D92525]">पोस्ट</span>
          </h3>
          <div className="mt-4 font-sans-dev text-[10px] md:text-[12px] tracking-[0.14em] md:tracking-[0.28em] uppercase text-white/60 break-words">
            The Denva Post · भरोसे की पत्रकारिता · Est. 2023
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 border-t border-white/15 pt-10 md:pt-12">
          <div className="col-span-12 md:col-span-4">
            <img src={LOGO_URL} alt="Denva Post" className="h-10 w-auto mb-6" />
            <p className="font-sans-dev text-[15px] leading-relaxed text-white/70 max-w-[380px]">
              नर्मदापुरम की धड़कन, मध्यप्रदेश की आवाज़ और देश-दुनिया की खबरें—एक ही जगह, भरोसे के साथ।
            </p>
          </div>

          {/* Site links */}
          <div className="col-span-6 md:col-span-2">
            <div className="eyebrow text-[#D92525] mb-5">वेबसाइट</div>
            <ul className="grid grid-cols-1 gap-y-3 font-sans-dev text-[14px] text-white/85">
              {SITE_LINKS.map((l) => (
                <li key={l.testKey}>
                  <a data-testid={`footer-link-${l.testKey}`} href={l.href} className="link-underline">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Policies (dedicated pages) */}
          <div className="col-span-6 md:col-span-3">
            <div className="eyebrow text-[#D92525] mb-5">नीतियाँ · Policies</div>
            <ul className="grid grid-cols-1 gap-y-3 font-sans-dev text-[14px] text-white/85">
              {POLICY_LINKS.map((l) => (
                <li key={l.testKey}>
                  <Link data-testid={`footer-link-${l.testKey}`} to={l.href} className="link-underline">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Socials */}
          <div className="col-span-12 md:col-span-3">
            <div className="eyebrow text-[#D92525] mb-5">फ़ॉलो करें</div>
            <ul className="space-y-3 font-sans-dev text-[14px] text-white/85">
              {SOCIALS.map(({ label, href, Icon }) => (
                <li key={label}>
                  <a data-testid={`footer-social-${label.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`} href={href} target="_blank" rel="noreferrer" className="flex items-center gap-3 group">
                    <span className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center group-hover:bg-[#D92525] group-hover:border-[#D92525] transition-colors">
                      <Icon size={15} />
                    </span>
                    <span className="link-underline">{label}</span>
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <GoogleFollow variant="footer" />
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 md:mt-16 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-3 md:gap-4 font-sans-dev text-[10px] md:text-[11px] uppercase tracking-[0.14em] md:tracking-[0.24em] text-white/50">
          <span data-testid="footer-copyright" className="break-words min-w-0">© 2026 Denva Post. All Rights Reserved.</span>
          <span className="text-white/40 break-words min-w-0">The Denva Post · भरोसे की पत्रकारिता</span>
        </div>
      </div>
    </footer>
  );
}
